import { z } from 'zod';
import { id, timestamp, paginatedResult } from './common.js';

export const cancellationWorkspace = z.enum(['professional', 'office']);
export type CancellationWorkspace = z.infer<typeof cancellationWorkspace>;
export const cancellationTarget = z.enum(['booking', 'shift']);
export type CancellationTarget = z.infer<typeof cancellationTarget>;
export const cancellationConfirmInput = z
  .object({
    workspace: cancellationWorkspace,
    reason: z
      .string()
      .trim()
      .min(3, 'Explain why you need to cancel (at least 3 characters)')
      .max(2000),
    previewToken: z.string().min(1).max(200),
    clientRequestId: z.string().uuid(),
  })
  .strict();
export type CancellationConfirmInput = z.infer<typeof cancellationConfirmInput>;
export const cancellationPreview = z.object({
  targetType: cancellationTarget,
  targetId: id,
  shiftId: id,
  bookingId: id.nullable(),
  locationName: z.string(),
  timezone: z.string(),
  startsAt: timestamp,
  status: z.string(),
  actorSide: cancellationWorkspace,
  canCancel: z.boolean(),
  reason: z.string(),
  policyVersion: z.string(),
  policyCode: z.string(),
  strikeWeight: z.number().nonnegative(),
  consequences: z.array(z.string()),
  financialDisclosure: z.string(),
  affectedBookingCount: z.number().int().nonnegative(),
  previewToken: z.string().nullable(),
  expiresAt: timestamp.nullable(),
  checkedAt: timestamp,
});
export type CancellationPreview = z.infer<typeof cancellationPreview>;
export const cancellationEvent = z.object({
  id,
  targetType: cancellationTarget,
  targetId: id,
  shiftId: id,
  bookingId: id.nullable(),
  actorSide: cancellationWorkspace,
  actorName: z.string(),
  actorUserId: id,
  reason: z.string(),
  policyVersion: z.string(),
  policyCode: z.string(),
  strikeWeight: z.number().nonnegative(),
  affectedBookingCount: z.number().int().nonnegative(),
  timezone: z.string(),
  createdAt: timestamp,
});
export type CancellationEvent = z.infer<typeof cancellationEvent>;
export const cancellationHistory = paginatedResult(cancellationEvent).extend({
  legacyNotice: z.string().nullable(),
});
export type CancellationHistoryView = z.infer<typeof cancellationHistory>;
export const cancellationResult = z.object({ event: cancellationEvent, replayed: z.boolean() });
export type CancellationResult = z.infer<typeof cancellationResult>;
